import DocumentFormConfig from "../components/finance/invoice/config/DocumentInvoice.config.json";
import MiscellaneousFormConfig from "../components/finance/invoice/config/MiscellaneousForm.config.json";
import { LookupDetails } from "../models/editable-table-header.interface";
import { FilterCondition } from "../models/table-list-request.interface";
import { Cycle, CycleType } from "./app-constants";
import { CustomerInvoiceFormConfig } from "../components/finance/invoice/config/CustomerInvoiceForm.config";
import { ContainerInvoiceFormConfig } from "../components/finance/invoice/config/ContainerInvoiceForm.config";

export const DocumentTypeDataListReq = {
  IGM_ITEM_NUMBER: {
    value: "IGM Item No",
    key: "IGM_ITEM_NUMBER",
    cycle: CycleType.IMPORT,
    lookupDetails: {
      apiUrl: "v1/import-cargo-items/list",
      selectFields: ["id", "igmItemNo"] as string[],
      filterByKeys: ["IGM_ITEM_NO_CONTAINS"] as string[],
      additionalFilters: [
        {
          "name": "DOCUMENT_TYPE_EQUALS",
          "value": ["BOE"],
          "alias": "documentType"
        }
      ],
      limit: 10,
      orderBy: { igmItemNo: "asc" },
      bindLabel: "igmItemNo",
      bindValue: "id",
      displayColumns: [
        { key: "igmItemNo", header: "IGM Item Number" }
      ] as { key: string; header: string }[]
    }
  },
  SHIPPING_BILL_NUMBER: {
    value: "Shipping Bill No",
    key: "SHIPPING_BILL_NUMBER",
    cycle: CycleType.EXPORT,
    lookupDetails: {
      apiUrl: "v1/items/search",
      selectFields: ["id", "documentNo"] as string[],
      filterByKeys: ["DOCUMENT_NO_CONTAINS"] as string[],
      limit: 10,
      orderBy: { documentNo: "asc" },
      additionalFilters: [
        {
          "name": "DOCUMENT_TYPE_EQUALS",
          "value": ["SHIPPING_BILL"],
          "alias": "documentType"
        }
      ],
      bindLabel: "documentNo",
      bindValue: "id",
      displayColumns: [
        { key: "documentNo", header: "Shipping Bill No" }
      ] as { key: string; header: string }[]
    }
  },
  BOE_NUMBER: {
    value: "BOE No",
    key: "BOE_NUMBER",
    cycle: CycleType.IMPORT,
    lookupDetails: {
      apiUrl: "v1/import-cargo-items/list",
      selectFields: ["id", "igmItemNo"] as string[],
      filterByKeys: ["DOCUMENT_NO_CONTAINS"] as string[],
      additionalFilters: [
        {
          "name": "DOCUMENT_TYPE_EQUALS",
          "value": ["BOE"],
          "alias": "documentType"
        }
      ],
      limit: 10,
      orderBy: { igmItemNo: "asc" },
      bindLabel: "boeNo",
      bindValue: "id",
      displayColumns: [
        { key: "boeNo", header: "BOE No" }
      ] as { key: string; header: string }[]
    } as LookupDetails
  },
  EXBOE_NUMBER: {
    value: "Ex BOE No",
    key: "EXBOE_NUMBER",
    cycle: CycleType.BOND,
    lookupDetails: {
      apiUrl: "v1/items/search",
      filterByKeys: ["DOCUMENT_NO_CONTAINS"] as string[],
      additionalFilters: [
        {
          "name": "DOCUMENT_TYPE_EQUALS",
          "value": ["EX_BOE"],
          "alias": "documentType"
        }
      ],
      limit: 10,
      orderBy: { documentNo: "asc" },
      bindLabel: "documentNo",
      bindValue: "id",
      displayColumns: [
        { key: "documentNo", header: "ExBOE No" }
      ] as { key: string; header: string }[]
    } as LookupDetails
  },
  CONTAINER_NUMBER: {
    value: "Container No",
    key: "CONTAINER_NUMBER",
    cycle: undefined,
    lookupDetails: {
      apiUrl: "v1/containers/list?useNewContract=true",
      selectFields: ["id", "containerNo"] as string[],
      filterByKeys: ["CONTAINER_NO_CONTAINS"] as string[],
      limit: 10,
      orderBy: { containerNo: "asc" },
      bindLabel: "containerNo",
      bindValue: "id",
      displayColumns: [
        { key: "containerNo", header: "Container Number" }
      ] as { key: string; header: string }[]
    }
  }
};

// Invoice Type System - Matching Backend Enum
export enum InvoiceType {
  IMPORT = "IMP",
  EXPORT = "EXP",
  BUFFER = "EXP",
  EMPTY = "EMT",
  BOND = "BND",
  PROFORMA = "PRO",
  MISCELLANEOUS = "OTH",
  DOCUMENT = "DOC",
  CUSTOMER = "CUS",
  CONTAINER = "CON"
}

export const getInvoiceTypeFromCycle = (cycle: string): InvoiceType | null => {
  console.log("getInvoiceTypeFromCycle: ", cycle);
  switch (cycle) {
    case CycleType.IMPORT.key:
      return InvoiceType.IMPORT;
    case CycleType.EXPORT.key:
    case CycleType.BUFFER.key:
      return InvoiceType.EXPORT;
    case CycleType.EMPTY.key:
      return InvoiceType.EMPTY;
    case CycleType.BOND.key:
      return InvoiceType.BOND;
    default:
      return null;
  }
}

export enum ViewMode {
  LIST = "list",
  CARD = "card"
}

export enum DeterminantType {
  TARIFF = "TARIFF",
  WORKFLOW = "WORKFLOW",
  STORAGE = "STORAGE",
  ALERT = "ALERT"
}

// Simplified invoice type configuration - only the main types needed
export const InvoiceTypeWithFormConfig = {
  [InvoiceType.DOCUMENT]: {
    value: "Document",
    key: "DOCUMENT",
    formConfig: DocumentFormConfig
  },
  [InvoiceType.PROFORMA]: {
    value: "Proforma",
    key: "PROFORMA",
    formConfig: DocumentFormConfig
  },
  [InvoiceType.MISCELLANEOUS]: {
    value: "Miscellaneous",
    key: "MISCELLANEOUS",
    formConfig: MiscellaneousFormConfig
  },
  [InvoiceType.CUSTOMER]: {
    value: "Customer",
    key: "CUSTOMER",
    formConfig: CustomerInvoiceFormConfig
  },
  [InvoiceType.CONTAINER]: {
    value: "Empty Container",
    key: "CONTAINER",
    formConfig: ContainerInvoiceFormConfig
  }
};

export function mapBackendTypeToFrontendType(backendType: string): InvoiceType {
  switch (backendType) {
    case "OTH":
      return InvoiceType.MISCELLANEOUS;
    case "PRO":
      return InvoiceType.PROFORMA;
    case "IMP":
    case "EXP":
    case "BND":
    case "EMT":
    default:
      return InvoiceType.DOCUMENT;
  }
}

// Map backend document types to frontend document types
export function mapBackendDocumentTypeToFrontend(backendDocType: string, isContainerWise: boolean = false): string {
  // If it's container-wise billing, return CONTAINER_NUMBER regardless of document type
  if (isContainerWise) {
    return "CONTAINER_NUMBER";
  }

  switch (backendDocType) {
    case "BOE":
      return "BOE_NUMBER";
    case "SHIPPING_BILL":
      return "SHIPPING_BILL_NUMBER";
    case "EX_BOE":
      return "EXBOE_NUMBER";

    default:
      return backendDocType;
  }
}

// Map frontend document types to backend document types
export function mapFrontendDocumentTypeToBackend(frontendDocType: string): string {
  switch (frontendDocType) {
    case "BOE_NUMBER":
      return "BOE";
    case "SHIPPING_BILL_NUMBER":
      return "SHIPPING_BILL";
    case "EXBOE_NUMBER":
      return "EX_BOE";
    case "CONTAINER_NUMBER":
      // Container is a UI-only type for container-wise billing
      // The actual document type should be determined from the selected document
      return "CONTAINER_NUMBER";
    default:
      return frontendDocType;
  }
}

export const DeterminantOperators = {
  EQUALS: {
    value: "EQUAL_TO",
    displayName: "Equal To",
    operator: "="
  },
  NOT_EQUALS: {
    value: "NOT_EQUAL_TO",
    displayName: "Not Equal To",
    operator: "!="
  },
  IN: {
    value: "EQUAL_TO",
    displayName: "In",
    operator: "IN"
  },
  NOT_IN: {
    value: "NOT_EQUAL_TO",
    displayName: "Not In",
    operator: "Not In"
  },
  GREATER_THAN: {
    value: "GREATER_THAN",
    displayName: "Greater Than",
    operator: ">"
  },
  GREATER_THAN_OR_EQUALS: {
    value: "GREATER_THAN_EQUAL_TO",
    displayName: "Greater Than Equals To",
    operator: ">="
  },
  LESS_THAN: {
    value: "LESS_THAN",
    displayName: "Less Than",
    operator: "<"
  },
  LESS_THAN_OR_EQUALS: {
    value: "LESS_THAN_EQUAL_TO",
    displayName: "Less Than Equals To",
    operator: "<="
  }
};

export const DeterminantDataTypes = {
  BOOLEAN: { inputType: "BOOLEAN", supportedOperators: [DeterminantOperators.EQUALS] },
  STRING: { inputType: "STRING", supportedOperators: [DeterminantOperators.EQUALS] },
  DROPDOWN: { inputType: "DROPDOWN", supportedOperators: [DeterminantOperators.EQUALS, DeterminantOperators.NOT_EQUALS] },
  REFERENCE: { inputType: "REFERENCE", supportedOperators: [DeterminantOperators.IN, DeterminantOperators.NOT_IN] },
  INTEGER: {
    inputType: "INTEGER",
    supportedOperators: [DeterminantOperators.EQUALS, DeterminantOperators.GREATER_THAN, DeterminantOperators.GREATER_THAN_OR_EQUALS, DeterminantOperators.LESS_THAN, DeterminantOperators.LESS_THAN_OR_EQUALS]
  },
  BIGDECIMAL: {
    inputType: "BIGDECIMAL",
    supportedOperators: [DeterminantOperators.EQUALS, DeterminantOperators.GREATER_THAN, DeterminantOperators.GREATER_THAN_OR_EQUALS, DeterminantOperators.LESS_THAN, DeterminantOperators.LESS_THAN_OR_EQUALS]
  },
  DATETIME: {
    inputType: "DATETIME",
    supportedOperators: [DeterminantOperators.EQUALS]
  },
  DATE: {
    inputType: "DATE",
    supportedOperators: [DeterminantOperators.EQUALS]
  },
  // Backend-only semantics for report range params: the FROM/TO ends of a date range.
  // Renders as a datetime input on the frontend (see reports-datatype.ts mapping).
  DATE_RANGE_FROM: {
    inputType: "DATE_RANGE_FROM",
    supportedOperators: [DeterminantOperators.EQUALS]
  },
  DATE_RANGE_TO: {
    inputType: "DATE_RANGE_TO",
    supportedOperators: [DeterminantOperators.EQUALS]
  }
};
export type DeterminantDataType = keyof typeof DeterminantDataTypes;

export const ReferenceData = {
  TRAILER_TYPE: { bindValue: "id", bindLabel: "name" },
  CONTAINER_TYPE: { bindValue: "type", bindLabel: "type" },
  vesselId: { bindValue: "id", bindLabel: "vesselName" },
  REPORT: {bindValue: "id", bindLabel: "name" }
};

export enum ChargeType {
  ADHOC = "ADHOC",
  SYSTEM = "SYSTEM"
}



